import { useLocation, useNavigate, useParams } from "react-router";
import { useForm } from "@mantine/form";
import { NumberInput, Flex, Textarea, Button } from "@mantine/core";
import { TimePicker } from "@mantine/dates";
import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import { deleteShiftById, updateShiftById } from "../../api/shiftAssignmentApi";
import { createNotification } from "../../utils/useNotification";
import ClockIcon from "../common/Icons/ClockIcon";
import Container from "../common/Container";
import PageHeader from "../common/PageHeader";

const ShiftEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { selectedEvent, selectedRole } = location.state || {};
  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm({
    initialValues: {
      startTime: "",
      endTime: "",
      unpaidBreakMinutes: 0,
      employeeCount: 1,
      workDuration: 0,
      notes: "",
    },
    validate: {
      startTime: (value: string) => (!value ? "Başlangıç saati gerekli" : null),
      endTime: (value: string) => (!value ? "Bitiş saati gerekli" : null),
      unpaidBreakMinutes: (value: number) => (value < 0 ? "Negatif olamaz" : null),
      employeeCount: (value: number) => (value <= 0 ? "Minimum 1 olmalı" : null),
      workDuration: (value: number) => (value < 0 ? "Negatif olamaz" : null),
    },
  });

  useEffect(() => {
    if (selectedEvent) {
      const start = dayjs(selectedEvent.startTime);
      const end = dayjs(selectedEvent.endTime);
      form.setValues({
        startTime: start.format("HH:mm"),
        endTime: end.format("HH:mm"),
        unpaidBreakMinutes: selectedEvent.unpaidBreakMinutes ?? 0,
        employeeCount: selectedEvent.count ?? 1,
        workDuration: end.diff(start, "minute") / 60,
        notes: selectedEvent.notes ?? "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { startTime, workDuration } = form.values;
    if (startTime && workDuration > 0) {
      const calculatedEnd = dayjs("2023-01-01T" + startTime)
        .add(workDuration * 60, "minute")
        .format("HH:mm");
      form.setFieldValue("endTime", calculatedEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.startTime, form.values.workDuration]);

  const handleSubmit = async (values: typeof form.values) => {
    const d = new Date(selectedEvent.startTime);
    const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const startDateTime = dayjs(`${dateString}T${values.startTime}`);
    const endDateTime = values.workDuration
      ? startDateTime.add(values.workDuration * 60, "minute").toDate()
      : dayjs(`${dateString}T${values.endTime}`).toDate();

    const updates = {
      id,
      startTime: startDateTime.format("YYYY-MM-DDTHH:mm:ss"),
      endTime: dayjs(endDateTime).format("YYYY-MM-DDTHH:mm:ss"),
      unpaidBreakMinutes: values.unpaidBreakMinutes,
      count: values.employeeCount,
      notes: values.notes,
    };

    try {
      const response: any = await updateShiftById({ id, updates });
      createNotification({ success: response?.success, message: response?.message });
      if (response?.success) navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    try {
      const response: any = await deleteShiftById({ id });
      createNotification({ success: response?.success, message: response?.message });
      if (response?.success) navigate(-1);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Container>
      <PageHeader
        pageTitle={dayjs(selectedEvent?.startTime).format("dddd, MMMM DD")}
        subText={selectedRole?.label ?? ""}
        isButtoned
        buttonText="Güncelle"
        buttonOnClick={() => formRef.current?.requestSubmit()}
      />
      <form ref={formRef} onSubmit={form.onSubmit(handleSubmit)}>
        <FieldSection>
          <SectionLabel>Mesai Saatleri</SectionLabel>
          <Flex gap={16} mb={20}>
            <TimePicker
              w="100%"
              label="Başlangıç Saati"
              {...form.getInputProps("startTime")}
              rightSection={<ClockIcon size={20} />}
            />
            <TimePicker
              w="100%"
              label="Bitiş Saati"
              {...form.getInputProps("endTime")}
              readOnly
              rightSection={<ClockIcon size={20} />}
            />
          </Flex>
          <FieldGrid>
            <NumberInput
              hideControls
              label="Çalışma Süresi (saat)"
              placeholder="Ör: 8"
              step={0.5}
              min={0}
              {...form.getInputProps("workDuration")}
            />
            <NumberInput
              hideControls
              label="Mola Süresi (dakika)"
              {...form.getInputProps("unpaidBreakMinutes")}
            />
            <NumberInput
              hideControls
              label="Çalışan Sayısı"
              {...form.getInputProps("employeeCount")}
            />
          </FieldGrid>
          <Textarea
            label="Notlar"
            placeholder="Vardiya için not girebilirsiniz."
            mt="xl"
            autosize
            minRows={3}
            maxRows={5}
            {...form.getInputProps("notes")}
          />
        </FieldSection>
      </form>

      <DeleteButton onClick={handleDelete}>Vardiyayı Sil</DeleteButton>
    </Container>
  );
};

export default ShiftEditPage;

const FieldSection = styled.div`
  margin-bottom: 24px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DeleteButton = styled(Button)<any>`
  margin-top: 24px;
  background-color: #fee2e2;
  color: #dc2626;
  &:hover {
    background-color: #fecaca;
  }
`;